import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react"; // npm i qrcode.react
import { PublicLayout } from "../../components/PublicLayout";
import { useSeo } from "../../lib/seo";

/**
 * petloc – Landing Page (Redesign)
 * --------------------------------------------------------------------------
 * Design-Referenz: design_handoff_landing_redesign/reference/petloc Landing.dc.html
 * Logo-Komponente: ../../components/PetlocMark  (aus PetlocMark.tsx)
 *
 * Voraussetzungen (siehe APPLY.md):
 *  1. Schrift "Spectral" laden  (index.html <link> + tailwind fontFamily.display)
 *  2. `npm i qrcode.react`
 *  3. Foto `karlo-hero.jpg` nach apps/client/public/  (liegt im Paket unter public/)
 *
 * Palette (warm, geerdet) – als arbitrary Tailwind values:
 *  paper #FBF8F5 · ink #1B0E16 · muted #6E5E66 · border #ECE3E7 · blush #FBEEFB
 *  accent = bestehende brand-Skala (brand-600 #c026d3 · brand-800 #86198f)
 */

// QR im Hero zeigt auf das Karlo-Demo-Profil — Besucher können es live scannen.
const DEMO_QR_URL = "https://petloc.co/c/qvfpns";

const STEPS = [
  { n: 1, t: "Account anlegen", d: "In wenigen Minuten registrieren und für jedes Haustier ein Profil mit Foto und Steckbrief anlegen." },
  { n: 2, t: "Halsband bestellen", d: "Sicherheitshalsband mit QR in deiner Wunschfarbe — einzeln 15 €, im 10er-Set nur 10 €/Stk." },
  { n: 3, t: "Benachrichtigt werden", d: "Sobald ein Finder scannt, kommt SMS und E-Mail mit GPS-Link direkt zu dir." },
];

const COLORS = [
  { name: "Rot", hex: "#DC2626" }, { name: "Orange", hex: "#EA580C" },
  { name: "Gelb", hex: "#EAB308" }, { name: "Grün", hex: "#16A34A" },
  { name: "Blau", hex: "#2563EB" }, { name: "Lila", hex: "#9333EA" },
  { name: "Pink", hex: "#EC4899" }, { name: "Schwarz", hex: "#1F1A1D" },
];

const FINDER_POINTS = [
  { h: "Anonym, wenn du willst", d: "Standardmäßig anonym — getrackt wird nichts. Eine Rückrufnummer ist rein optional." },
  { h: "Standort in einem Tipp", d: "Eine einzige Position wird sicher an dich übermittelt." },
  { h: "Foto, Nachricht & Kontakt optional", d: "„Sitzt im Hinterhof, wirkt gesund\" — beruhigt sofort." },
];

/** Pfoten-Glyph (Scan-Rahmen) für die kleinen Inline-Stellen. Farbe via currentColor. */
function PawScan({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 56 56" fill="none" aria-hidden="true">
      <g stroke="currentColor" strokeWidth="4.2" strokeLinecap="round" fill="none">
        <path d="M6 17V10a4 4 0 0 1 4-4h7" /><path d="M39 6h7a4 4 0 0 1 4 4v7" />
        <path d="M50 39v7a4 4 0 0 1-4 4h-7" /><path d="M17 50h-7a4 4 0 0 1-4-4v-7" />
      </g>
      <g fill="currentColor">
        <ellipse cx="20.5" cy="23" rx="3.7" ry="4.7" /><ellipse cx="35.5" cy="23" rx="3.7" ry="4.7" />
        <ellipse cx="14" cy="31.5" rx="3.2" ry="4" /><ellipse cx="42" cy="31.5" rx="3.2" ry="4" />
        <path d="M28 28.5c-5.2 0-9.4 3.6-9.4 8.2 0 3.4 2.9 5 6.2 5 1.6 0 2.4-.6 3.2-.6s1.6.6 3.2.6c3.3 0 6.2-1.6 6.2-5 0-4.6-4.2-8.2-9.4-8.2Z" />
      </g>
    </svg>
  );
}

export function LandingPage() {
  useSeo({
    description:
      "QR-Sicherheitshalsband und Mini-Anhänger für Katzen und Hunde: Wer dein Tier findet, scannt mit dem Handy und schickt dir Standort und Nachricht in Sekunden. Kein Akku, kein Abo, ab 10 €.",
    path: "/",
  });
  return (
    <PublicLayout>
      <div className="bg-[#FBF8F5] text-[#211019] -my-8">
        {/* HERO */}
        <section className="relative mx-auto max-w-[1140px] px-7 pt-20 pb-16 grid md:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
          <div className="pointer-events-none absolute -top-20 -right-28 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(217,70,239,0.13),rgba(217,70,239,0)_68%)]" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#FBEEFB] border border-[#F3D7F2] pl-3 pr-3.5 py-1.5 text-[13.5px] font-semibold text-brand-800">
              <span className="h-[7px] w-[7px] rounded-full bg-brand-600 animate-pulse" />
              QR-Sicherheitshalsband für jedes Haustier
            </div>
            <h1 className="font-display font-medium text-[clamp(40px,6vw,60px)] leading-[1.04] tracking-[-0.018em] mt-5 text-[#1B0E16] text-balance">
              Bring verlorene Haustiere wieder nach <span className="text-brand-600 italic">Hause</span>
            </h1>
            <p className="mt-6 text-[19px] leading-relaxed text-[#5C4D55] max-w-[480px]">
              Ein robustes Halsband mit speziellem QR-Code – mehr nicht. Wer dein Tier findet, scannt mit dem
              Handy und schickt dir <strong className="text-[#2E2027] font-semibold">Standort und Nachricht</strong> in
              Sekunden. Anonym für den Finder, sofort bei dir.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" className="btn-primary rounded-[18px] px-6 py-[15px] text-base shadow-[0_8px_22px_rgba(192,38,211,0.28)]">Jetzt schützen</Link>
              <a href="#how" className="btn-secondary rounded-[18px] px-6 py-[15px] text-base">Wie es funktioniert</a>
            </div>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-[0_6px_16px_rgba(192,38,211,0.25)]">
              💜 Kein Abo – du zahlst einmal, fertig
            </div>
            <ul className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium text-[#6E5E66]">
              {["Ab 10 € pro Halsband", "Sicherheitsverschluss", "SMS & E-Mail Alarm"].map((f) => (
                <li key={f} className="inline-flex items-center gap-2"><span className="text-brand-600 text-[17px]">✓</span> {f}</li>
              ))}
            </ul>
          </div>

          {/* Hero visual: echtes Foto + scanbarer QR + Notification */}
          <div className="relative h-[520px]">
            <div className="absolute inset-0 rounded-[28px] overflow-hidden border border-[#ECE3E7] shadow-[0_30px_60px_-30px_rgba(60,20,50,0.35)]">
              <img src="/karlo-hero.jpg" alt="Katze Karlo mit petloc QR-Sicherheitshalsband" className="w-full h-full object-cover object-[50%_38%]" />
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-[rgba(27,14,22,0.45)] to-transparent">
                <span className="font-mono text-[11.5px] tracking-wide text-white bg-black/30 px-2 py-1 rounded-md">Karlo trägt petloc</span>
              </div>
            </div>
            {/* floating QR card (scanbar → Karlo) */}
            <div className="absolute top-7 -left-8 bg-white rounded-[18px] p-4 border border-[#F0E6EC] shadow-[0_18px_40px_-16px_rgba(60,20,50,0.4)]">
              <QRCodeSVG value={DEMO_QR_URL} size={116} fgColor="#1B0E16" bgColor="#ffffff" level="L" />
              <div className="mt-2.5 text-center font-mono text-[10.5px] tracking-[0.06em] text-[#8A7A82]">SCAN MICH</div>
            </div>
            {/* notification toast */}
            <div className="absolute bottom-7 -right-8 bg-white rounded-2xl px-4 py-3.5 flex items-center gap-3 border border-[#F0E6EC] shadow-[0_18px_40px_-16px_rgba(60,20,50,0.4)] max-w-[264px]">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-[#ECFDF3] flex items-center justify-center text-[#16A34A] text-xl">📍</div>
              <div>
                <div className="text-sm font-semibold text-[#211019]">Standort empfangen</div>
                <div className="text-[12.5px] text-[#6E5E66] mt-px">Karlo wurde 1,2 km entfernt gescannt</div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how" className="mx-auto max-w-[1140px] px-7 pt-14 pb-5">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-9">
            <div>
              <div className="font-mono text-[12.5px] tracking-[0.14em] uppercase text-brand-600 font-semibold">In drei Schritten</div>
              <h2 className="font-display font-medium text-[38px] leading-tight tracking-[-0.015em] mt-3 text-[#1B0E16]">So einfach ist der Heimweg</h2>
            </div>
            <p className="max-w-[360px] text-[15.5px] leading-relaxed text-[#6E5E66]">Kein Tracker, kein Akku, kein Abo-Zwang. Nur ein robustes Halsband, das im Ernstfall den Unterschied macht.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} className="relative bg-white border border-[#ECE3E7] rounded-[18px] px-6 pt-7 pb-8 shadow-[0_1px_2px_rgba(60,20,50,0.04)]">
                <div className="flex items-center justify-between">
                  <div className="w-[46px] h-[46px] rounded-[13px] bg-[#FBEEFB] text-brand-800 flex items-center justify-center font-display text-[22px] font-semibold">{s.n}</div>
                  <div className="font-mono text-[11px] tracking-[0.1em] text-[#C9B9C2]">0{s.n} / 03</div>
                </div>
                <h3 className="text-[19px] font-semibold tracking-[-0.01em] mt-5 text-[#211019]">{s.t}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[#6E5E66]">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FINDER EXPERIENCE (dark) */}
        <section className="mt-[76px] bg-[#1B0A1F] text-[#F4E9F2]">
          <div className="mx-auto max-w-[1140px] px-7 py-[72px] grid md:grid-cols-[0.9fr_1.1fr] gap-16 items-center">
            <div className="flex justify-center">
              <div className="w-[286px] bg-[#0F050F] rounded-[42px] p-[11px] border border-[#3A1F3D] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)]">
                <div className="bg-[#FBF8F5] rounded-[32px] overflow-hidden h-[564px] relative">
                  <div className="h-[30px] flex items-center justify-center">
                    <div className="w-[92px] h-1.5 rounded-full bg-[#E2D6DD] mt-2" />
                  </div>
                  <div className="px-5 pt-1.5">
                    <div className="flex items-center gap-2">
                      <div className="h-[22px] w-[22px] rounded-md bg-brand-600" />
                      <span className="text-sm font-semibold text-[#211019]">petloc</span>
                    </div>
                    <div className="mt-5 text-center">
                      <div className="w-[76px] h-[76px] rounded-full mx-auto border border-[#E9DDE5] bg-[repeating-linear-gradient(135deg,#EFE2EC_0_8px,#F7F0F4_8px_16px)]" />
                      <h4 className="font-display font-semibold text-[22px] mt-4 text-[#1B0E16]">Du hast Karlo gefunden!</h4>
                      <p className="text-[13px] leading-snug text-[#6E5E66] mt-2 mx-3.5">Danke, dass du scannst! Mit wenigen Klicks gibst du dem Besitzer Bescheid.</p>
                    </div>
                    <div className="mt-[18px] bg-brand-600 text-white rounded-2xl p-3.5 text-center text-[15px] font-semibold shadow-[0_8px_18px_rgba(192,38,211,0.35)]">📍 Standort teilen</div>
                    <div className="mt-2.5 bg-white border border-[#ECE3E7] rounded-2xl px-3.5 py-3 text-[12.5px] text-[#8A7A82] leading-snug">Einmalig & ohne Tracking. Standort jetzt — Nummer nur, wenn du erreichbar sein willst.</div>
                    <div className="mt-2.5 bg-white border border-[#ECE3E7] rounded-2xl px-3.5 py-2.5 text-[13px] text-[#B6A6AE]">Kurze Nachricht (optional)…</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="font-mono text-[12.5px] tracking-[0.14em] uppercase text-brand-400 font-semibold">Für den Finder</div>
              <h2 className="font-display font-medium text-[40px] leading-[1.08] tracking-[-0.015em] mt-3.5 text-[#FBEFF7]">Ein Tipp — und dein Tier ist fast zu Hause</h2>
              <p className="mt-5 text-[17px] leading-relaxed text-[#C9AEC2] max-w-[460px]">Keine App, kein Login — der Finder braucht kein Konto. Wer dein Haustier findet, scannt den QR-Code und tippt einmal. Mehr braucht es nicht.</p>
              <div className="mt-[30px] grid gap-3.5 max-w-[460px]">
                {FINDER_POINTS.map((it, i) => (
                  <div key={i} className="flex gap-3.5 items-start">
                    <span className="shrink-0 w-[30px] h-[30px] rounded-[9px] bg-brand-400/15 text-brand-400 flex items-center justify-center font-semibold text-sm">{i + 1}</span>
                    <div>
                      <div className="text-[15.5px] font-semibold text-[#F4E9F2]">{it.h}</div>
                      <div className="text-sm text-[#B79CB0] mt-0.5">{it.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* PRODUCT & PRICING */}
        <section id="produkt" className="mx-auto max-w-[1140px] px-7 py-20">
          <div className="mb-12 overflow-hidden rounded-[28px] border border-[#ECE3E7] shadow-[0_30px_60px_-30px_rgba(60,20,50,0.35)]">
            <img
              src="/produkt-halsband.jpg"
              alt="petloc QR-Sicherheitshalsband mit graviertem Anhänger"
              className="w-full h-[300px] md:h-[380px] object-cover object-center"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-14 items-center">
            <div>
              <div className="font-mono text-[12.5px] tracking-[0.14em] uppercase text-brand-600 font-semibold">Das Halsband</div>
              <h2 className="font-display font-medium text-[40px] leading-[1.08] tracking-[-0.015em] mt-3.5 text-[#1B0E16]">Robust gebaut, fair bepreist</h2>
              <p className="mt-[18px] text-[16.5px] leading-relaxed text-[#5C4D55] max-w-[470px]">
                Reflektierendes Halsband mit dauerhaft eingraviertem QR-Code. Der Sicherheitsverschluss{" "}
                <strong className="text-[#2E2027] font-semibold">reißt ab, sobald dein Tier irgendwo hängen bleibt</strong> — das
                schützt vor Strangulation, das Halsband geht dabei aber verloren. Heißt: am besten gleich ein paar auf Vorrat.
              </p>

              <div className="mt-7 grid gap-3 max-w-[440px]">
                {[
                  <><strong className="font-semibold">Sicherheitsverschluss</strong> — reißt ab, wenn dein Tier hängen bleibt. Keine Strangulationsgefahr.</>,
                  <><strong className="font-semibold">Staffelrabatt</strong> bis 10 Stück — ein abgerissenes Halsband will ersetzt werden. Vorrat lohnt sich.</>,
                  <><strong className="font-semibold">Eigener Code je Band</strong> — verlierst du eins, sperrst du genau dieses; die anderen laufen weiter.</>,
                  <>Name optional aufs Halsband gedruckt.</>,
                ].map((node, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white border border-[#ECE3E7] rounded-2xl px-4 py-3.5">
                    <span className="shrink-0 text-brand-600 text-lg">⦿</span>
                    <span className="text-[15px] text-[#2E2027]">{node}</span>
                  </div>
                ))}
              </div>

              {/* Halsband-Typ: Hund / Katze */}
              <div className="mt-[26px]">
                <div className="text-[13px] text-[#8A7A82] font-medium mb-3">Halsband-Typ</div>
                <div className="flex gap-2.5 flex-wrap">
                  <span className="inline-flex items-center gap-2.5 px-4 py-[11px] rounded-xl bg-[#FBEEFB] border-[1.5px] border-brand-600 text-brand-800 text-[15px] font-semibold">
                    <DogIcon /> Für Hunde
                  </span>
                  <span className="inline-flex items-center gap-2.5 px-4 py-[11px] rounded-xl bg-white border-[1.5px] border-[#E4D8DE] text-[#2E2027] text-[15px] font-semibold hover:border-brand-600 transition-colors">
                    <CatIcon /> Für Katzen
                  </span>
                </div>
                <div className="mt-2 text-[13px] text-[#8A7A82]">Passform, Breite und Verschluss passend zum Tier — beim Bestellen wählbar.</div>
              </div>

              {/* Farben */}
              <div className="mt-[26px]">
                <div className="text-[13px] text-[#8A7A82] font-medium mb-3">Acht Farben</div>
                <div className="flex flex-wrap gap-2.5">
                  {COLORS.map((c) => (
                    <div key={c.name} title={c.name} className="w-[34px] h-[34px] rounded-full"
                      style={{ background: c.hex, boxShadow: "0 0 0 1px rgba(0,0,0,0.06), 0 0 0 4px #FBF8F5, 0 0 0 5px #ECE3E7" }} />
                  ))}
                </div>
              </div>

              {/* Mini-Anhänger für bestehende Halsbänder */}
              <div className="mt-[26px] max-w-[440px] flex items-start gap-[15px] bg-white border border-[#ECE3E7] rounded-2xl px-5 py-[18px]">
                <div className="shrink-0 w-11 h-11 rounded-xl bg-[#FBEEFB] flex items-center justify-center text-brand-600">
                  <PawScan size={24} />
                </div>
                <div>
                  <div className="text-base font-semibold text-[#1B0E16]">Mein Tier hat aber schon ein Halsband</div>
                  <div className="mt-1 text-[14.5px] leading-relaxed text-[#5C4D55]">
                    Dann bestell nur den <strong className="font-semibold text-[#2E2027]">Mini-Anhänger</strong> mit QR-Code — leicht,
                    klein und einfach am vorhandenen Halsband befestigt. Gleicher Schutz, ohne neues Halsband.
                  </div>
                  <Link to="/signup" className="inline-block mt-2.5 text-[14.5px] font-semibold text-brand-600 hover:text-brand-800">Mini-Anhänger ansehen →</Link>
                </div>
              </div>
            </div>

            {/* price card */}
            <div className="bg-white border border-[#ECE3E7] rounded-[26px] p-[34px] shadow-[0_30px_60px_-34px_rgba(60,20,50,0.3)]">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm text-[#8A7A82] font-medium">Ab</div>
                  <div className="font-display text-[56px] font-semibold leading-none text-[#1B0E16] tracking-[-0.02em]">10 €</div>
                  <div className="text-sm text-[#8A7A82] mt-1.5">pro Halsband im 10er-Set · einzeln 15 €</div>
                </div>
                <div className="bg-[#FBEEFB] text-brand-800 text-[12.5px] font-semibold px-3 py-1.5 rounded-full">Staffelpreis</div>
              </div>
              <div className="h-px bg-[#EFE5EB] my-6" />
              <div className="grid gap-2.5">
                {[
                  ["QR-Halsband mit Sicherheitsverschluss", true],
                  ["Online-Profil je Haustier", true],
                  ["E-Mail-Alarm bei Fund", true],
                  ["SMS-Alarm (50 ct / SMS)", false],
                ].map(([label, on]) => (
                  <div key={label as string} className="flex items-center justify-between text-[15px]">
                    <span className="text-[#5C4D55]">{label}</span>
                    {on ? <span className="text-brand-600 font-semibold">✓</span> : <span className="text-[#B6A6AE] font-semibold">optional</span>}
                  </div>
                ))}
              </div>
              <div className="mt-4 text-[13px] text-[#8A7A82] leading-relaxed">Je mehr, desto günstiger: Staffelpreis sinkt fließend bis 10 € pro Stück im 10er-Set.</div>
              <div className="mt-5 flex items-start gap-2.5 bg-[#FBEEFB] border border-[#F3D7F2] rounded-[13px] px-3.5 py-3 text-[13.5px] leading-snug text-brand-800">
                <span className="text-[15px] leading-tight">⦿</span>
                <span><strong className="font-semibold">Empfohlen: 3–5 Stück als Vorrat.</strong> So liegt immer ein Ersatz bereit, falls sich das Sicherheitshalsband unterwegs löst.</span>
              </div>
              <Link to="/signup" className="btn-primary mt-[18px] w-full rounded-[18px] py-[15px] text-base shadow-[0_8px_20px_rgba(192,38,211,0.28)]">Account erstellen &amp; bestellen</Link>
              <div className="mt-3 text-center text-[13px] text-[#8A7A82]">Kostenlos registrieren — zahlst nur das Halsband.</div>
              <div className="mt-5 pt-4 border-t border-[#EFE5EB] text-[13px] leading-relaxed text-[#6E5E66]">
                <strong className="font-semibold text-[#2E2027]">Tierheim, Katzenschutz oder Züchter?</strong> Das 10er-Set für 100 € gibt jedem vermittelten Tier ein Halsband mit. Jedes Band hat einen eigenen Code — die neuen Besitzer übernehmen das Profil einfach.{" "}
                <a href="mailto:info@wegzwei.com?subject=10er-Set%20f%C3%BCr%20Tierheim%20%2F%20Z%C3%BCchter" className="font-semibold text-brand-600 hover:text-brand-800">Kurz anfragen →</a>
              </div>
            </div>
          </div>
        </section>

        {/* RATGEBER (SEO-Einstiege) */}
        <section className="mx-auto max-w-[1140px] px-7 mb-16">
          <div className="font-mono text-[12.5px] tracking-[0.14em] uppercase text-brand-600 font-semibold">Ratgeber</div>
          <h2 className="font-display font-medium text-[34px] leading-tight tracking-[-0.015em] mt-3 text-[#1B0E16]">Gut vorbereitet, bevor es ernst wird</h2>
          <div className="mt-7 grid md:grid-cols-2 gap-5">
            {[
              { to: "/katze-vermisst", t: "Katze vermisst: Was du jetzt tun solltest", d: "Die Checkliste für die ersten 24 Stunden — Umgebung, Nachbarn, Register, Plakate. Und was danach hilft." },
              { to: "/qr-anhaenger-vs-tasso-gps", t: "QR-Anhänger, Tasso-Chip oder GPS-Tracker?", d: "Was welche Lösung leistet, was sie kostet und warum die Kombination am meisten bringt." },
            ].map((g) => (
              <Link key={g.to} to={g.to} className="block bg-white border border-[#ECE3E7] rounded-[18px] px-6 py-6 no-underline hover:border-brand-300 transition-colors">
                <div className="text-[18px] font-semibold tracking-[-0.01em] text-[#211019]">{g.t}</div>
                <p className="mt-2 text-[15px] leading-relaxed text-[#6E5E66]">{g.d}</p>
                <span className="inline-block mt-3 text-[14.5px] font-semibold text-brand-600">Weiterlesen →</span>
              </Link>
            ))}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="mx-auto max-w-[1140px] px-7 mb-[84px]">
          <div className="relative overflow-hidden rounded-[30px] bg-gradient-to-br from-brand-600 to-brand-800 px-12 py-16 text-center">
            <div className="absolute -top-16 -left-10 h-60 w-60 rounded-full bg-white/[0.08]" />
            <div className="absolute -bottom-24 -right-8 h-[300px] w-[300px] rounded-full bg-white/[0.07]" />
            <h2 className="relative font-display font-medium text-[44px] leading-[1.08] tracking-[-0.015em] text-white text-balance">Gib deinem Tier den schnellsten Heimweg</h2>
            <p className="relative mt-4 mx-auto text-lg leading-snug text-[#F6E2F4] max-w-[480px]">In wenigen Minuten eingerichtet. Bei der nächsten Begegnung mit einem Finder zählt jede Sekunde.</p>
            <div className="relative mt-[30px] flex justify-center gap-3 flex-wrap">
              <Link to="/signup" className="bg-white text-brand-800 font-semibold rounded-[18px] px-7 py-[15px] text-base no-underline shadow-[0_10px_24px_rgba(0,0,0,0.18)]">Jetzt loslegen</Link>
              <a href="#how" className="text-white bg-white/[0.14] border border-white/35 font-semibold rounded-[18px] px-6 py-[15px] text-base no-underline">So funktioniert's</a>
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}

/* --- kleine Tier-Icons für den Halsband-Typ-Wähler --- */
function DogIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4.5 6.2c.9-.5 2.2.2 2.9 1.5.3.6.4 1.2.4 1.7 1.2-.5 2.6-.8 4.2-.8s3 .3 4.2.8c0-.5.1-1.1.4-1.7.7-1.3 2-2 2.9-1.5.9.5 1 2 .3 3.3-.4.7-.9 1.2-1.5 1.5.5.9.8 1.9.8 3 0 3.3-3.2 5.5-7.1 5.5S5 16.8 5 13.5c0-1.1.3-2.1.8-3-.6-.3-1.1-.8-1.5-1.5-.8-1.3-.7-2.8.2-3.3Z" />
    </svg>
  );
}
function CatIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M5.5 4.3c.3-.2.7-.1.9.2L8.7 8c1-.4 2.1-.6 3.3-.6s2.3.2 3.3.6l2.3-3.5c.2-.3.6-.4.9-.2.3.2.4.5.4.9l-.3 4.6c.7 1 1.1 2.2 1.1 3.5 0 3.4-3.4 5.7-7.7 5.7S4.3 16.7 4.3 13.3c0-1.3.4-2.5 1.1-3.5L5.1 5.2c0-.4.1-.7.4-.9Z" />
    </svg>
  );
}
